import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { FunctionComponent, MutableRefObject, useCallback, useEffect } from "react";
import { SubCategoryMap, isCompatibleOption } from "../utils/options";
import {
  OptionListItem,
  OptionListItem_SubCategory_Option,
} from "./ProductOption/ProductOption";
import {
  useGqlField,
  CoreInlineNumber,
} from "@shared/components";
import { gql } from "@shared/utils";
import { PRODUCT_CONFIGURATION_VIEW_INNER } from "./productOptionQuerys";
import { useProductOptionContext } from "./ProductOption/ProductOptionContext";

const UNSET_KEY = "unset";

// Type for the imperative handle exposed by OptionSelect
export type OptionSelectHandle = {
  cancelDebounces: (original: { options: string[]; variableType: string; number: number; text: string; unit: string }) => void;
};

const REPLACE_OPTIONS_LIST_PRODUCT = gql`
mutation ReplaceOptionsListProduct($id: String!, $value: [String!]! , $subCategoryId: String! ) {
  replaceOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId optionIds: $value }) {
    ${PRODUCT_CONFIGURATION_VIEW_INNER}
  }
}
`;

const SET_VARIABLE_TYPE = gql`
mutation SetVariableType( $id: String!, $value: String!, $subCategoryId: String! ) {
  updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, variableType: $value }) {
    ${PRODUCT_CONFIGURATION_VIEW_INNER}
  }
}
`;

const SET_NUMBER = gql`
mutation SetVariableType( $id: String!, $value: Float!, $subCategoryId: String! ) {
  updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, number: $value }) {
    ${PRODUCT_CONFIGURATION_VIEW_INNER}
  }
}
`;

const SET_UNIT = gql`
mutation SetVariableType( $id: String!, $value: String!, $subCategoryId: String! ) {
  updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, unit: $value }) {
    ${PRODUCT_CONFIGURATION_VIEW_INNER}
  }
}
`;

const SET_TEXT = gql`
mutation SetVariableType( $id: String!, $value: String!, $subCategoryId: String! ) {
  updateOptionsListProductConfiguration(input: { id: $id, subCategoryId: $subCategoryId, text: $value }) {
    ${PRODUCT_CONFIGURATION_VIEW_INNER}
  }
}
`;

export const OptionSelect: FunctionComponent<{
  optionList: OptionListItem;
  productId: string;
  catmap: SubCategoryMap;
  readonly?: boolean;
  originalItems?: MutableRefObject<Record<string, { options: string[]; variableType: string; number: number; text: string; unit: string }>>;
  onBeforeChange?: () => void;
}> = ({ optionList, catmap, productId, readonly, originalItems, onBeforeChange }) => {
  const theme = useTheme();
  const { registerOptionSelect } = useProductOptionContext();
  
  const [options, setOptions, optionsUtils] = useGqlField(
    optionList.options || [],
    productId,
    REPLACE_OPTIONS_LIST_PRODUCT,
    {
      variables: {
        subCategoryId: optionList?.SubCategory?._id,
      },
    }
  );
  const [variableType, setVariableType, variableTypeUtils] = useGqlField(
    optionList.variableType || (optionList as unknown as { variabletype?: string }).variabletype || "none",
    productId,
    SET_VARIABLE_TYPE,
    {
      variables: {
        subCategoryId: optionList?.SubCategory?._id,
      },
    }
  );

  const [number, setNumber, numberUtils] = useGqlField(
    optionList.number || 0,
    productId,
    SET_NUMBER,
    {
      variables: {
        subCategoryId: optionList?.SubCategory?._id,
      },
    }
  );

  const [unit, setUnit, unitUtils] = useGqlField(
    optionList.unit || "",
    productId,
    SET_UNIT,
    {
      variables: {
        subCategoryId: optionList?.SubCategory?._id,
      },
    }
  );
  const [text, setText, textUtils] = useGqlField(
    optionList.text || "",
    productId,
    SET_TEXT,
    {
      variables: {
        subCategoryId: optionList?.SubCategory?._id,
      },
    }
  );

  // Register this OptionSelect with the context so Cancel can cancel our debounces
  useEffect(() => {
    const subCategoryId = optionList?.SubCategory?._id;
    if (subCategoryId) {
      registerOptionSelect(subCategoryId, {
        cancelDebounces: (original) => {
          optionsUtils.cancelAndReset(original.options);
          variableTypeUtils.cancelAndReset(original.variableType);
          numberUtils.cancelAndReset(original.number);
          textUtils.cancelAndReset(original.text);
          unitUtils.cancelAndReset(original.unit);
        },
      });
      return () => {
        registerOptionSelect(subCategoryId, null);
      };
    }
  }, [optionList?.SubCategory?._id, registerOptionSelect, optionsUtils, variableTypeUtils, numberUtils, textUtils, unitUtils]);

  const compatibleOptions: Array<
    OptionListItem_SubCategory_Option & { compatible: boolean }
  > = [...(optionList.SubCategory?.Options ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((o) => {
      const compatible = isCompatibleOption(catmap, {
        subCategoryId: optionList.SubCategory!._id,
        optionId: o._id,
      });
      return { compatible, ...o };
    });

  const setDefaultOptions = useCallback(
    (optionId: string) => {
      // Fire immediately on user interaction, before mutation
      onBeforeChange?.();
      
      if (!optionId || optionId === UNSET_KEY) {
        setOptions([]);
        return;
      }
      const option = optionList.SubCategory?.Options.find(
        (o) => o._id === optionId
      );
      // Handle legacy naming convention (variabletype vs variableType)
      const optionsWithLegacy = options as unknown as { variabletype?: string; variableType?: string };
      if (optionsWithLegacy.variabletype && !optionsWithLegacy.variableType) {
        optionsWithLegacy.variableType = optionsWithLegacy.variabletype;
      }
      if (option?.variableType) {
        setVariableType(option.variableType);
      }
      if (option?.number) {
        setNumber(option.number);
      }
      if (option?.unit) {
        setUnit(option.unit);
      }
      if (option?.text) {
        setText(option.text);
      }
      setOptions([optionId]);
    },
    [JSON.stringify(options)]
  );

  useEffect(() => {
    const onlyCompatible = compatibleOptions.filter((o) => o.compatible);
    const currentSelection = optionList.options[0];
    const currentSelectionIsValid = currentSelection && onlyCompatible.find((o) => o._id === currentSelection);

    // Case 1: No selection yet and only one compatible option - auto-select it
    if (onlyCompatible.length === 1 && optionList.options.length === 0) {
      setDefaultOptions(onlyCompatible[0]._id);
      return;
    }

    // Case 2: Current selection is invalid - auto-correct to a valid option
    if (
      optionList.options.length === 1 &&
      !currentSelectionIsValid &&
      onlyCompatible.length >= 1
    ) {
      // Prefer non-custom options when multiple choices exist
      const firstNotCustom = onlyCompatible.find(
        (o) => !o.variableType || o.variableType === "none"
      );
      if (firstNotCustom) {
        setDefaultOptions(firstNotCustom._id);
      } else {
        setDefaultOptions(onlyCompatible[0]._id);
      }
    }
  }, [
    compatibleOptions.filter((o) => o.compatible).length,
    optionList.options.length,
    JSON.stringify(compatibleOptions.filter((o) => o.compatible)),
  ]);

  const originalItem = originalItems?.current[optionList.SubCategory?._id ?? ''];
  const isChanged = !!originalItem && (
    JSON.stringify([...options].sort()) !== JSON.stringify([...(originalItem.options || [])].sort()) ||
    (variableType || "none") !== (originalItem.variableType || "none") ||
    (number || 0) !== (originalItem.number || 0) ||
    (text || "") !== (originalItem.text || "") ||
    (unit || "") !== (originalItem.unit || "")
  );
  const changedBorderSx = isChanged
    ? {
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.info.main,
          borderWidth: "2px",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.info.main,
        },
      }
    : {};

  if (optionList.SubCategory?.maxOptions === 1) {
    const value = options[0] || UNSET_KEY;
    return (
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1 }}>
          <FormControl sx={{ width: "100%", ...changedBorderSx }}>
            <InputLabel id={`os-label-${optionList.SubCategory!._id}`}>
              {optionList.SubCategory!.name}
            </InputLabel>
            <Select
              size="small"
              disabled={readonly}
              error={value === UNSET_KEY}
              value={value}
              labelId={`os-label-${optionList.SubCategory!._id}`}
              sx={{ width: "100%" }}
              label={`${optionList.SubCategory!.name}`}
              onChange={(e) => {
                setDefaultOptions(e.target.value);
              }}
            >
              <MenuItem value={UNSET_KEY}>Select One</MenuItem>
              {compatibleOptions.map((o, i) => {
                return (
                  <MenuItem
                    key={`option-${o._id || `index-${i}`}`}
                    value={o._id}
                    disabled={!o.compatible}
                  >
                    {o.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
        {variableType === "number" && (
          <Box sx={{ flexGrow: 2 }}>
            <FormControl sx={{ width: "100%" }}>
              <CoreInlineNumber
                number={number}
                unit={unit}
                onNumberChange={setNumber}
                onUnitChange={setUnit}
              />
            </FormControl>
          </Box>
        )}
        {variableType === "text" && (
          <Box sx={{ flexGrow: 2 }}>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                value={text}
                onChange={(e) => setText(e.target.value)}
                size="small"
              />
            </FormControl>
          </Box>
        )}
      </Box>
    );
  }
  return <Select></Select>;
};
